import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";

export function cn(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(" ");
}

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 rounded-full font-medium transition disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap";

const VARIANTS = {
  primary:
    "bg-[linear-gradient(90deg,var(--accent),var(--accent-2))] text-white shadow-[0_8px_30px_-8px_var(--accent)] hover:brightness-110",
  secondary:
    "bg-white/8 text-foreground border border-line-strong hover:bg-white/14",
  ghost: "text-muted hover:text-foreground hover:bg-white/6",
} as const;

const SIZES = {
  sm: "h-8 px-3.5 text-[13px]",
  md: "h-10 px-5 text-sm",
  lg: "h-12 px-7 text-[15px]",
} as const;

type ButtonStyle = {
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
};

export function Button({
  variant = "primary",
  size = "md",
  className,
  ...props
}: ComponentProps<"button"> & ButtonStyle) {
  return (
    <button
      className={cn(BUTTON_BASE, VARIANTS[variant], SIZES[size], className)}
      {...props}
    />
  );
}

export function LinkButton({
  variant = "primary",
  size = "md",
  className,
  ...props
}: ComponentProps<typeof Link> & ButtonStyle) {
  return (
    <Link
      className={cn(BUTTON_BASE, VARIANTS[variant], SIZES[size], className)}
      {...props}
    />
  );
}

export function Card({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("card", className)} {...props} />;
}

export function Badge({
  tone = "neutral",
  className,
  ...props
}: ComponentProps<"span"> & {
  tone?: "neutral" | "accent" | "success" | "warning" | "danger";
}) {
  const tones = {
    neutral: "bg-white/8 text-muted",
    accent: "bg-[var(--accent)]/18 text-[#c9b8ff]",
    success: "bg-[var(--success)]/16 text-[var(--success)]",
    warning: "bg-[var(--warning)]/16 text-[var(--warning)]",
    danger: "bg-[var(--danger)]/16 text-[var(--danger)]",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium",
        tones[tone],
        className,
      )}
      {...props}
    />
  );
}

export function Progress({ value }: { value: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/8">
      <div
        className="h-full rounded-full bg-[linear-gradient(90deg,var(--accent),var(--accent-2))] transition-[width] duration-500"
        style={{ width: `${Math.max(2, value)}%` }}
      />
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-block size-4 animate-spin rounded-full border-2 border-white/25 border-t-white",
        className,
      )}
    />
  );
}

export function SectionTitle({
  eyebrow,
  title,
  desc,
}: {
  eyebrow?: string;
  title: ReactNode;
  desc?: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      {eyebrow && (
        <div className="mb-3 text-[13px] font-medium tracking-wide text-[#b9a6ff]">
          {eyebrow}
        </div>
      )}
      <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
        {title}
      </h2>
      {desc && <p className="mt-4 text-[15px] leading-7 text-muted">{desc}</p>}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">{label}</span>
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

export const inputClass =
  "w-full rounded-xl border border-line bg-white/4 px-4 py-3 text-sm outline-none transition placeholder:text-white/30 focus:border-[var(--accent)] focus:bg-white/6";
